import React, { useMemo, useState } from 'react';
import { Search, Package as PackageIcon, Trash2, MinusCircle, User } from 'lucide-react';
import { toast } from 'sonner';
import { redeemPackageSession, deletePackage } from '../../lib/salesStorage';
import { money } from '../../lib/services';

export default function PackagesTab({ packages, onChange }) {
  const [query, setQuery] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showRemainingOnly, setShowRemainingOnly] = useState(true);

  const filtered = useMemo(() => {
    let list = packages;
    if (showRemainingOnly) list = list.filter((p) => p.sessionsRemaining > 0);
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) =>
      [p.clientName, p.serviceName, p.id].some((f) =>
        String(f || '').toLowerCase().includes(q)
      )
    );
  }, [packages, query, showRemainingOnly]);

  const handleRedeem = (id) => {
    const { pkg } = redeemPackageSession(id);
    onChange?.();
    if (pkg && pkg.sessionsRemaining >= 0) {
      toast.success(
        `Session redeemed — ${pkg.sessionsRemaining} of ${pkg.sessionsPurchased} remaining.`
      );
    }
  };

  const handleDelete = (id) => {
    deletePackage(id);
    setConfirmDelete(null);
    onChange?.();
    toast.success('Package deleted.');
  };

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div className="relative w-full lg:max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search packages by client or service..."
            className="w-full h-14 pl-12 pr-4 rounded-2xl border border-white/10 bg-white text-slate-100 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 focus:outline-none"
            data-testid="packages-search-input"
          />
        </div>
        <label className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-white border border-white/10 cursor-pointer text-sm font-semibold text-slate-200 select-none">
          <input
            type="checkbox"
            checked={showRemainingOnly}
            onChange={(e) => setShowRemainingOnly(e.target.checked)}
            className="w-4 h-4 accent-blue-500"
            data-testid="packages-active-only-toggle"
          />
          Active only
        </label>
      </div>

      <div className="bg-white rounded-2xl border border-white/10 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="px-6 py-16 text-center text-slate-400">
            {packages.length === 0
              ? 'No packages yet. Sell a multi-session package to create one.'
              : 'No packages match your filters.'}
          </div>
        ) : (
          <div data-testid="packages-list">
            {filtered.map((p) => {
              const pct = Math.round((p.sessionsRemaining / p.sessionsPurchased) * 100);
              const depleted = p.sessionsRemaining === 0;
              return (
                <div
                  key={p.id}
                  className="border-b border-white/10 last:border-b-0 px-6 py-5 flex flex-col md:flex-row md:items-center gap-4"
                  data-testid={`packages-row-${p.id}`}
                >
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${
                      depleted
                        ? 'bg-slate-900 border border-white/10 text-slate-400'
                        : 'bg-blue-500/20/70 border border-blue-500/30 text-blue-400'
                    }`}>
                      <PackageIcon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white truncate">
                        {p.serviceName}
                      </p>
                      <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5 truncate">
                        <User className="w-3 h-3" />
                        {p.clientName}
                      </p>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Purchased {new Date(p.createdAt).toLocaleDateString()} · Paid {money(p.totalPaid)} (−{p.discountPct}%)
                      </p>
                    </div>
                  </div>

                  <div className="md:w-64">
                    <div className="flex items-baseline justify-between mb-1">
                      <p className="text-xs uppercase tracking-widest text-slate-400 font-bold">Balance</p>
                      <p
                        className={`text-lg font-display ${depleted ? 'text-slate-400' : 'text-white'}`}
                        data-testid={`packages-balance-${p.id}`}
                      >
                        <span className="text-2xl">{p.sessionsRemaining}</span>
                        <span className="text-slate-400"> / {p.sessionsPurchased}</span>
                      </p>
                    </div>
                    <div className="h-2 rounded-full bg-slate-900 overflow-hidden">
                      <div
                        className={`h-full ${depleted ? 'bg-slate-300' : 'bg-blue-500'} transition-all`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2 md:justify-end">
                    <button
                      onClick={() => handleRedeem(p.id)}
                      disabled={depleted}
                      className="pill-btn inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-blue-500 hover:bg-blue-500 text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`packages-redeem-${p.id}`}
                    >
                      <MinusCircle className="w-4 h-4" />
                      Redeem 1
                    </button>
                    <button
                      onClick={() => setConfirmDelete(p)}
                      className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-white hover:bg-red-50 hover:text-red-600 text-slate-400 border border-white/10"
                      data-testid={`packages-delete-${p.id}`}
                      title="Delete package"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {confirmDelete && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setConfirmDelete(null)}
          data-testid="packages-delete-modal"
        >
          <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-8 py-6">
              <h3 className="font-display text-2xl text-slate-100">Delete package?</h3>
              <p className="mt-2 text-slate-300">
                Remove the <span className="font-semibold">{confirmDelete.serviceName}</span> package for <span className="font-semibold">{confirmDelete.clientName}</span>. This does not refund the client.
              </p>
              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setConfirmDelete(null)} className="h-11 px-5 rounded-full bg-slate-900 hover:bg-slate-800 text-slate-200 font-semibold text-sm">Cancel</button>
                <button onClick={() => handleDelete(confirmDelete.id)} className="h-11 px-5 rounded-full bg-red-500 hover:bg-red-600 text-white font-semibold text-sm" data-testid="packages-delete-confirm">Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
